import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LoadingController, ToastController } from "ionic-angular";
import { Observable } from "rxjs/Observable";
import { ConfigurationService } from "../configuration/configuration.service";
import { StateManagementService } from "../state-management/state-management.service";

// Matches Spring `spring.servlet.multipart.maxFileSize=10MB` in document-management.
const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

@Injectable()
export class DocManagementDataService {
  spinner = null;
  spinnerCounter = 0;

  assessmentId: string;
  patChartId: string;
  dataUrl: string;

  constructor(
    public http: HttpClient,
    public toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    public stateData: StateManagementService,
    private config: ConfigurationService
  ) {
    if (
      this.config.getConfig().isLocal &&
      this.config.getConfig().localEndpoints.docMgt
    ) {
      this.dataUrl = this.config.getConfig().localEndpoints.docMgt;
    } else {
      this.dataUrl = this.config.getConfig().apiUrl;
    }
    if (this.stateData.getChartId()) {
      this.patChartId = this.stateData.getChartId();
    } else {
      this.stateData.getChartIdObserver().subscribe((data) => {
        this.patChartId = data;
      });
    }
    // NOVA-1490: only write if we have a real value — avoids persisting the
    // string "undefined" when the observer hasn't fired yet. Also switched
    // from bracket syntax to setItem so instrumentation can see the write.
    if (this.patChartId != null) {
      sessionStorage.setItem("patChartId", String(this.patChartId));
    }
  }

  public getDocumentList(
    id: string,
    pageLimit: number,
    currentPage: number,
    sortBy: string,
    sortDirection: string,
    ignoreVisibility: boolean = false
  ) {
    let headers = new HttpHeaders().set("Content-Type", "application/json");

    let params = new HttpParams()
      .set("key", id)
      .set("pageLimit", pageLimit.toString())
      .set("currentPage", currentPage.toString())
      .set("sortBy", sortBy)
      .set("sortDirection", sortDirection)
      .set("ignoreVisibility", String(ignoreVisibility));

    return new Observable((observer) => {
      this.http
        .get(`${this.dataUrl}/api/document/get`, {
          headers: headers,
          params: params,
        })
        .subscribe(
          (data) => {
            observer.next(data);
            observer.complete();
          },
          (err: HttpErrorResponse) => {
            this.presentErrorToast(err, "Failed to load data.");
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public getDocument(id: string) {
    try {
      return this.http.get(`${this.dataUrl}/api/document/get/${id}`, {
        observe: "response",
        responseType: "arraybuffer",
      });
    } catch (error) {
      console.log("Error during data submission and building.", error);
    }
  }

  public getDocumentPDFById(id: string) {
    return this.http.get(`${this.dataUrl}/api/document/get/${id}`, {
      observe: "response",
      responseType: "blob",
    });
  }

  public getDocumentPDF(id: number) {
    try {
      let params = new HttpParams().set("claimId", id.toString());

      return this.http.get(`${this.dataUrl}/claims/generate/pdf`, {
        observe: "response",
        responseType: "blob",
        params,
      });
    } catch (error) {
      console.log("Error during data submission and building.", error);
    }
  }

  public getDocumentToView(id: string) {
    try {
      return this.http.get(`${this.dataUrl}/api/document/get/${id}`, {
        responseType: "arraybuffer",
      });
    } catch (error) {
      console.log("Error during data submission and building.", error);
    }
  }

  uploadDocument(data: FormData) {
    return new Observable((observer) => {
      // NOVA-1571: fail fast on oversized uploads so the user sees a specific
      // size error instead of a generic toast after a network round-trip.
      let largestSize = 0;
      data.forEach((value) => {
        if (value instanceof Blob && value.size > largestSize) {
          largestSize = value.size;
        }
      });
      if (largestSize > MAX_UPLOAD_SIZE_BYTES) {
        // Display in decimal MB to match what macOS/Windows show the user —
        // a file Finder calls "10.5 MB" should read as 10.5 here, not the
        // 10.0 binary-MiB rendering.
        const sizeMb = (largestSize / 1000000).toFixed(1);
        const toast = this.toastCtrl.create({
          message: `File is too large (${sizeMb} MB). Maximum allowed is 10 MB. Please split or compress the file and try again.`,
          duration: 6000,
          position: "below",
          cssClass: "error",
        });
        toast.present();
        observer.error(new Error("File exceeds maximum upload size"));
        observer.complete();
        return;
      }

      this.increaseSpinnerCount("Uploading Document...");
      this.http.post(`${this.dataUrl}/api/document/upload`, data).subscribe(
        (data) => {
          this.presentSuccessToast("Successfully uploaded the document.");
          observer.next(data);
          observer.complete();
          this.decreaseSpinnerCount();
        },
        (err) => {
          this.presentErrorToast(err, "Failed to upload the document.");
          this.decreaseSpinnerCount();
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  deleteSelectedDocument(deletelist: string) {
    return new Observable((observer) => {
      this.increaseSpinnerCount("Deleting Document...");
      this.http
        .delete(`${this.dataUrl}/api/document/softDelete?${deletelist}`)
        .subscribe(
          (data) => {
            this.presentSuccessToast("Successfully deleted.");
            this.decreaseSpinnerCount();
            observer.next(data);
            observer.complete();
          },
          (err) => {
            this.presentErrorToast(err, "Failed to delete.");
            this.decreaseSpinnerCount();
            observer.error(err);
            observer.complete();
          }
        );
    });
  }

  public viewDocument(id) {
    this.increaseSpinnerCount("Loading File...");
    this.getDocumentToView(id).subscribe(
      (data) => {
        const file = new Blob([data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
        this.decreaseSpinnerCount();
      },
      (err) => {
        this.decreaseSpinnerCount();
        this.presentErrorToast(err, "View document failed");
      }
    );
  }

  presentSuccessToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000,
      position: "below",
      cssClass: "success",
    });

    toast.present();
  }

  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      return;
    }
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        message += "Server error.";
      }
    }
    // NOVA-1571: surface server-side `message` when present. HttpClient
    // auto-parses JSON responses, so `err.error` is usually an object — the
    // prior string-only branch never fired for 4xx responses.
    const serverMessage = this.extractServerMessage(err);
    if (serverMessage) {
      message = serverMessage;
    }
    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  private extractServerMessage(err: HttpErrorResponse): string | null {
    if (!err || !err.error) {
      return null;
    }
    if (typeof err.error === "string") {
      try {
        const parsed = JSON.parse(err.error);
        return parsed && typeof parsed.message === "string"
          ? parsed.message
          : null;
      } catch {
        return null;
      }
    }
    if (typeof err.error === "object" && typeof err.error.message === "string") {
      return err.error.message;
    }
    return null;
  }

  increaseSpinnerCount(message: string = "Loading data...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/doc-management-data-service/doc-management-data-service.ts